/** @jsxImportSource @emotion/react */
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Typography
} from 'antd';
import dayjs from 'dayjs';
const { RangePicker } = DatePicker;

interface HistoryChatBoxSearchProps {
  form: FormInstance;
  onSearch?: (filter: ChatHistoryFilter) => void;
}

export const HistoryChatBoxSearch: React.FC<HistoryChatBoxSearchProps> = ({
  form,
  onSearch
}) => {
  const handleSearch = () => {
    const values = form.getFieldsValue();

    const startAt = values.timeRange?.[0]
      ? values.timeRange[0].toISOString()
      : undefined;
    const endAt = values.timeRange?.[1]
      ? values.timeRange[1].toISOString()
      : undefined;

    const newFilter: ChatHistoryFilter = {
      from: startAt ? dayjs(startAt).format('YYYY-MM-DD') : undefined,
      to: endAt ? dayjs(endAt).format('YYYY-MM-DD') : undefined,
      keyword: values.keyword || ''
    };

    if (onSearch) {
      onSearch(newFilter);
    }
  };

  const handleClear = () => {
    form.resetFields();
    if (onSearch) {
      onSearch({});
    }
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <Typography.Title css={titleStyles} level={5}>
          BỘ LỌC LỊCH SỬ CHAT
        </Typography.Title>
      ),
      children: (
        <Form form={form} css={formStyles}>
          <Row gutter={16} align='stretch'>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Thời gian'
                name='timeRange'
                css={customLabel}
                labelCol={{ span: 6 }}
                colon={false}
              >
                <RangePicker
                  allowEmpty={[true, true]}
                  format='YYYY-MM-DD'
                  placeholder={['Từ', 'Đến']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={8} css={filterItem}>
              <Form.Item
                name='keyword'
                label='Tìm kiếm'
                css={customLabel}
                labelCol={{ span: 6 }}
                colon={false}
              >
                <Input
                  placeholder='Từ khóa trong câu hỏi/trả lời...'
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={16}
            align='middle'
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem'
            }}
          >
            <Button
              type='default'
              icon={<ReloadOutlined />}
              css={resetButton}
              onClick={handleClear}
            >
              Đặt lại
            </Button>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              css={searchButton}
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
          </Row>
        </Form>
      )
    }
  ];

  return (
    <Collapse
      items={items}
      defaultActiveKey={'1'}
      expandIconPosition={'end'}
      css={collapseStyles}
    />
  );
};

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const formStyles = css`
  margin-top: 0.5rem;
  padding: 1rem;
  gap: 1rem;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const resetButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;
